import { useMemo, useState } from "react";
import AppDrawer from "./AppDrawer";
import { DataTransforms, Module } from "../../types/TenantInterfaces";
import { Alert, Button, Card, CardBody } from "reactstrap";
import { Link } from "react-router-dom";
import useModule from "../../hooks/useModule";
import TransformRecordEditDrawer from "./Records/TransformRecordEditDrawer";
import { newToastEvent, updateToastEvent } from "../../utils/ToastUtil";
import { SelectPicker } from "rsuite";
import { getBulkDTPrefill } from "../../utils/DataTransformsUtils";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import useCreateRecords from "../../api/api-hooks/records/useCreateRecords";
import AppSpinner from "../../elements/AppSpinner";
import { schemaAtom } from "../../atoms/GlobalAtoms";
import { useAtomValue } from "jotai";
import InstanceUtils from "../../utils/InstanceUtils";

interface Props {
  open: boolean;
  currentModule: Module | undefined;
  customDataTransform?: DataTransforms;
  records: Array<any>;
  onClose: () => void;
}

const empty: [] = [];

const BulkDataTransforms = ({
  open,
  currentModule,
  customDataTransform,
  records,
  onClose,
}: Props) => {
  const { createRecords, recordsCreating, createRecordsError, clearError } =
    useCreateRecords();
  const [openRecord, setOpenRecord] = useState<{ open: boolean; record: any }>({
    open: false,
    record: undefined,
  });
  const [dataTransform, setDataTransform] = useState<DataTransforms | null>(
    null
  );
  const [currentRecords, setCurrentRecords] = useState<Record<string, any>[]>();
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [errorTransforming, setErrorTransforming] = useState<string>();

  const schema = useAtomValue(schemaAtom);

  const { module: targetModule } = useModule(
    dataTransform?.TargetModule,
    true,
    false,
    false
  );

  const filter = useMemo(() => {
    if (!records || records.length === 0 || !open) {
      return undefined;
    }

    return {
      Fields: ["*"],
      Filters: records.map((record) => {
        return {
          Group: true,
          Filters: [
            {
              Field: "id",
              Method: "equals",
              Value: record.id || record._id,
            },
          ],
        };
      }),
    };
  }, [records, open]);

  const { records: fetchedRecords, recordsLoading } = useQueriedRecords({
    moduleAPIName: currentModule?.APIName || "",
    query: filter,
    disabled: filter === undefined,
  });

  const recordMap: any = useMemo(() => {
    const out: Record<string, {}> = {};
    if (!records) return out;
    records?.forEach((record: any) => {
      out[record?.id || record?._id] = {
        _RecordName: record._RecordName,
        id: record?.id || record?._id,
      };
    });

    return out;
  }, [records]);

  const availableDataTransforms: DataTransforms[] = useMemo(() => {
    if (!currentModule) return empty;

    const availableTransforms = currentModule.DataTransforms
      ? currentModule?.DataTransforms.filter(
          (dt) =>
            dt.SourceModule === currentModule.APIName &&
            dt.TargetModule !== currentModule.APIName
        ) || empty
      : empty;

    if (customDataTransform) {
      availableTransforms.push(customDataTransform);
    }

    return availableTransforms;
  }, [schema, currentModule, customDataTransform]);

  return (
    <>
      <AppDrawer
        open={open}
        onClose={handleClose}
        size="md"
        backdrop="static"
        headerTitle={
          dataTransform
            ? `Convert ${dataTransform?.SourceModule} to ${dataTransform?.TargetModule}`
            : "Select a Data Transform"
        }
        headerOptions={
          <Button
            onClick={convertData}
            disabled={recordsCreating || !dataTransform || prefillLoading}
            color="primary"
            size="sm"
            style={{ width: 100 }}
          >
            Convert
          </Button>
        }
        body={
          <div className="h-100">
            <div className="d-flex align-items-center ml-2 mb-3">
              <h4 className="m-0 text-nowrap">Data Transform: </h4>
              {availableDataTransforms?.length > 0 ? (
                <SelectPicker
                  data={availableDataTransforms.map((dt) => ({
                    label: dt.Name,
                    value: dt,
                  }))}
                  className="w-100 ml-2"
                  onChange={(value) => {
                    if (!value) return setDataTransform(null);
                    setDataTransform(value);
                    if (value) handleDataTransformChange(value);
                  }}
                  searchable={false}
                  placeholder="Select Transform"
                  defaultOpen
                />
              ) : (
                <div className="ml-2 p-0">No Data Transforms Available</div>
              )}
            </div>
            {recordsLoading && dataTransform && (
              <div className="d-flex flex-column align-items-center w-100 mt-6">
                <AppSpinner size="lg" />
                <h5 className="mt-4">Loading selected records...</h5>
              </div>
            )}

            <Alert
              isOpen={errorTransforming || createRecordsError ? true : false}
              toggle={() => {
                setErrorTransforming(undefined);
                clearError();
              }}
              color="danger"
            >
              {errorTransforming ||
                createRecordsError ||
                "Error Transforming Record"}
            </Alert>

            {prefillLoading && (
              <div className="d-flex flex-column mt-5 align-items-center">
                <h2 className="py-2">Transforming your data</h2>
                <AppSpinner size="xl" />
              </div>
            )}

            {dataTransform &&
              recordMap &&
              !prefillLoading &&
              !recordsCreating &&
              Object.keys(recordMap)?.map((record: any, i: number) => (
                <BulkDataTransformsRecordCard
                  key={recordMap[record]?.id || i}
                  recordName={recordMap[record]?._RecordName}
                  onReview={() => editRecord(recordMap[record].id)}
                  disabled={errorTransforming || createRecordsError}
                />
              ))}
          </div>
        }
      />

      {targetModule && openRecord && dataTransform && currentModule && (
        <TransformRecordEditDrawer
          open={openRecord.open}
          onClose={() => setOpenRecord({ open: false, record: undefined })}
          dataTransform={dataTransform}
          sourceModule={currentModule}
          currentData={openRecord.record}
          targetModule={targetModule}
          onSaveRecord={(values) =>
            onSaveRecord(values, openRecord.record?._id)
          }
        />
      )}
    </>
  );

  function handleClose() {
    setOpenRecord({
      open: false,
      record: undefined,
    });
    setDataTransform(null);
    setCurrentRecords(undefined);
    setPrefillLoading(false);
    setErrorTransforming(undefined);
    onClose();
  }

  function handleDataTransformChange(dataTransform: DataTransforms) {
    const foundTModule = schema?.CRM.Modules.find(
      (m) => m.APIName === dataTransform.TargetModule
    );

    if (!foundTModule || !currentModule) return;

    setPrefillLoading(true);

    getBulkDTPrefill({
      records: fetchedRecords,
      dataTransform,
      targetModule: foundTModule,
      sourceModule: currentModule,
      onSuccess: (newRecords: any) => {
        setCurrentRecords(newRecords);
        setPrefillLoading(false);
      },
      onError: (error: any) => {
        console.error(error);
        setPrefillLoading(false);
      },
    });
  }

  function editRecord(id: string) {
    setOpenRecord({
      open: true,
      record: (currentRecords ? currentRecords : fetchedRecords).find(
        (r: any) => r._id === id
      ),
    });
  }

  function onSaveRecord(values: any, id: string) {
    Object.keys(values).forEach((key) => {
      if (
        values[key] === "" ||
        values[key] === null ||
        values[key] === undefined
      ) {
        delete values[key];
      }
    });

    setCurrentRecords((prev: any) =>
      prev.map((record: any) => {
        if (record.id === id) {
          return { ...record, ...values };
        }
        return record;
      })
    );
  }

  async function convertData() {
    if (!targetModule || !dataTransform || !currentRecords) return;

    setPrefillLoading(true);
    newToastEvent("info", "Converting your data", dataTransform?.id);

    const finalRecords = currentRecords.map((r) => {
      delete r._id;
      delete r.id;
      delete r._RecordName;
      return r;
    });

    const promises = finalRecords.map(async (record) => {
      return InstanceUtils.formatRecordCreationPayload(
        targetModule.Fields,
        record,
        true
      );
    });

    const formattedRecords = await Promise.all(promises);

    if (!formattedRecords) {
      updateToastEvent(
        "error",
        "Error transforming your records",
        dataTransform?.id
      );
      return;
    }

    createRecords({
      // @ts-ignore
      payload: formattedRecords,
      moduleAPIName: targetModule.APIName,
      onCreate: () => {
        setPrefillLoading(false);
        handleClose();
        updateToastEvent(
          "success",
          "Successfully transformed your records",
          dataTransform?.id
        );
      },
      onError: (error: any) => {
        setPrefillLoading(false);
        console.error(error);
        updateToastEvent(
          "error",
          "Error transforming your records",
          dataTransform?.id
        );
      },
    });
  }
};

export const BulkDataTransformsRecordCard = ({
  recordName,
  disabled = false,
  onReview,
}: {
  recordName?: string;
  disabled?: boolean;
  onReview: () => void;
}) => {
  return (
    <div className="d-flex align-items-center ">
      <Card
        className="w-100 m-2"
        style={{ border: "1px solid #e8edea", height: "50px" }}
      >
        <CardBody className="h-100 w-100 m-0 pl-3 d-flex align-items-center">
          <Link to="#">{recordName || "(No Name)"}</Link>
        </CardBody>
      </Card>
      <Button disabled={disabled} onClick={onReview} size="sm">
        Review
      </Button>
    </div>
  );
};
export default BulkDataTransforms;
